import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import io from "socket.io-client";

import PixAndGoHeader from "@/components/molecules/PixAndGoHeader";
import PixAndGoLayout from "@/components/molecules/PixAndGoLayout";
import PixAndGoStepper from "@/components/molecules/PixAndGoStepper";
import { ReactComponent as CopyIcon } from "@/assets/images/copy.svg";
import { ReactComponent as ErrorIcon } from "@/assets/images/error.svg";
import i18n from "@/translate/i18n";
import { convertMsToTime, copyTextToClipboard } from "@/utils";
import {
  createPixAndGoTransaction,
  getPixAndGoStatus,
} from "@/services/pixAndGoApi";
import usePixAndGoSession from "@/hooks/usePixAndGoSession";

const ONE_SECOND = 1000;
const THIRTY_MINUTES = 1800000;
const CURRENCY = "BRL";

function PixAndGoQR() {
  const history = useHistory();
  const location = useLocation();
  const { merchantSlug, uuid: urlUuid } = useParams();

  const amount = location.state?.amount;

  const {
    save: saveSession,
    get: getSession,
    clear: clearSession,
  } = usePixAndGoSession(merchantSlug);

  const [qrData, setQrData] = useState(null);
  const [counter, setCounter] = useState(THIRTY_MINUTES);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    setQrData(null);
    setCounter(THIRTY_MINUTES);
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    i18n.changeLanguage("pt-BR");
  }, []);

  useEffect(() => {
    const session = getSession();

    if (session) {
      const restoreQrFromSession = () => {
        setQrData({
          ulid: session.ulid,
          qr_code: session.qrCode,
          amount: session.amount,
          currency: session.currency,
          expires_at: session.expiresAt,
        });
        const ms = Math.max(0, new Date(session.expiresAt) - Date.now());
        setCounter(ms);
        if (!urlUuid) {
          history.replace(`/pix-and-go/${merchantSlug}/qr/${session.ulid}`);
        }
      };

      getPixAndGoStatus(session.ulid)
        .then((res) => {
          const { status } = res.data;
          if (status === "form_required") {
            history.push({
              pathname: `/pix-and-go/${session.ulid}/form`,
              state: { merchantSlug },
            });
            return;
          }
          if (
            status === "registering_user" ||
            status === "deposit_in_progress"
          ) {
            history.push({
              pathname: `/pix-and-go/${session.ulid}/processing`,
              state: { status, merchantSlug },
            });
            return;
          }
          if (
            status === "completed" ||
            status === "user_registration_failed" ||
            status === "deposit_failed"
          ) {
            history.push({
              pathname: `/pix-and-go/${session.ulid}/status`,
              state: { status, merchantSlug },
            });
            return;
          }
          restoreQrFromSession();
        })
        .catch((err) => {
          const isNotFound = err?.response?.status === 404;
          if (isNotFound || !err?.response) {
            restoreQrFromSession();
          } else {
            setError(i18n.t("pixAndGo.qr.error"));
          }
        })
        .finally(() => setLoading(false));
      return;
    }

    if (!amount) {
      history.replace(`/pix-and-go/${merchantSlug}`);
      return;
    }

    createPixAndGoTransaction(merchantSlug, { amount, currency: CURRENCY })
      .then((res) => {
        const { data } = res;
        setQrData(data);
        saveSession({
          ulid: data.ulid,
          qrCode: data.qr_code,
          amount: data.amount,
          currency: data.currency || CURRENCY,
          expiresAt: data.expires_at,
        });
        history.replace(`/pix-and-go/${merchantSlug}/qr/${data.ulid}`);
        if (data.expires_at) {
          const ms = Math.max(0, new Date(data.expires_at) - Date.now());
          setCounter(ms);
        }
      })
      .catch(() => setError(i18n.t("pixAndGo.qr.error")))
      .finally(() => setLoading(false));
  }, [
    merchantSlug,
    amount,
    urlUuid,
    getSession,
    saveSession,
    history,
    retryCount,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const qrUuid = qrData?.ulid;

  useEffect(() => {
    if (!qrUuid) return undefined;

    const socket = io(process.env.REACT_APP_SERVER_URL, {
      path: "/websocket/socket.io",
    });

    socket.on("confirmTransaction", (socketData) => {
      if (socketData.event !== "pix_and_go" || socketData.ulid !== qrUuid)
        return;

      const { status } = socketData;

      if (status === "form_required") {
        socket.disconnect();
        history.push({
          pathname: `/pix-and-go/${qrUuid}/form`,
          state: { merchantSlug },
        });
      } else if (
        status === "registering_user" ||
        status === "deposit_in_progress"
      ) {
        socket.disconnect();
        history.push({
          pathname: `/pix-and-go/${qrUuid}/processing`,
          state: { status, merchantSlug },
        });
      } else if (
        status === "completed" ||
        status === "user_registration_failed" ||
        status === "deposit_failed"
      ) {
        socket.disconnect();
        history.push({
          pathname: `/pix-and-go/${qrUuid}/status`,
          state: { status, merchantSlug },
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [qrUuid, history, merchantSlug]);

  useEffect(() => {
    if (!qrData) return undefined;
    if (counter <= 0) {
      clearSession();
      history.push({
        pathname: `/pix-and-go/${qrData.ulid}/status`,
        state: { status: "expired", merchantSlug },
      });
      return undefined;
    }
    const timer = setInterval(
      () => setCounter((prev) => prev - ONE_SECOND),
      ONE_SECOND,
    );
    return () => clearInterval(timer);
  }, [counter, qrData, history, merchantSlug, clearSession]);

  const handleCopy = useCallback(() => {
    if (qrData?.qr_code) {
      copyTextToClipboard(qrData.qr_code, () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [qrData?.qr_code]);

  return (
    <PixAndGoLayout merchantSlug={merchantSlug}>
      <PixAndGoHeader />
      <PixAndGoStepper currentStep={2} />
      <main className="flex flex-col items-center justify-center flex-1 px-6 pb-10 sm:px-12">
        {loading && (
          <p className="text-center text-pag-on-background-muted">
            {i18n.t("pixAndGo.qr.loading")}
          </p>
        )}

        {error && (
          <div className="w-full max-w-lg text-center flex flex-col items-center gap-6">
            <ErrorIcon className="w-24 h-24" />
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-pag-danger">
                {i18n.t("pixAndGo.qr.errorTitle")}
              </h1>
              <p className="text-base text-pag-on-background-muted leading-relaxed">
                {i18n.t("pixAndGo.qr.errorMessage")}
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full">
              <button
                type="button"
                onClick={handleRetry}
                className="w-full py-4 rounded-full bg-pag-primary text-pag-on-primary font-bold text-base"
              >
                {i18n.t("pixAndGo.status.retry")}
              </button>
              {process.env.REACT_APP_SUPPORT_URL && (
                <a
                  href={process.env.REACT_APP_SUPPORT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-3 rounded-full border-2 border-pag-danger text-pag-danger font-semibold text-base flex items-center justify-center"
                >
                  {i18n.t("pixAndGo.qr.errorSupport")}
                </a>
              )}
            </div>
          </div>
        )}

        {qrData && (
          <div className="w-full max-w-sm flex flex-col items-center gap-5">
            <h1 className="text-2xl text-center text-pag-on-background leading-snug">
              <span className="font-light">
                {i18n.t("pixAndGo.qr.titleLight")}
              </span>{" "}
              <span className="font-bold">
                {i18n.t("pixAndGo.qr.titleBold")}
              </span>
            </h1>

            <div className="border border-pag-on-background-muted/60 p-1 rounded">
              <div className="bg-white p-3">
                <QRCode value={qrData.qr_code || ""} size={200} />
              </div>
            </div>

            <p className="text-sm text-pag-on-background-muted text-center">
              {i18n.t("pixAndGo.qr.timer", {
                time: convertMsToTime(counter),
              })}
            </p>

            <div className="w-full bg-pag-surface rounded-lg px-4 py-3">
              <p className="text-sm text-pag-on-surface text-center leading-relaxed">
                {i18n.t("pixAndGo.qr.disclaimer")}
              </p>
            </div>

            <button
              type="button"
              onClick={handleCopy}
              className="w-full bg-pag-surface border-2 border-pag-primary rounded-lg px-8 py-6 flex items-center gap-4"
            >
              <CopyIcon className="w-8 h-8 text-pag-on-surface shrink-0" />
              <span className="text-pag-on-surface font-bold text-xl">
                {copied ? i18n.t("general.copied") : i18n.t("pixAndGo.qr.copy")}
              </span>
            </button>
          </div>
        )}
      </main>
    </PixAndGoLayout>
  );
}

export default PixAndGoQR;
